import classNames from 'classnames/bind';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  COOKIE_CONSENT_CHANGE_EVENT_NAME,
  COOKIE_CONSENT_REQUEST_EVENT_NAME,
  type CookieConsentCategories,
  type CookieConsentCategory,
  createCookieConsentRecord,
  readCookieConsent,
  writeCookieConsent,
} from './state';
import styles from './styles.less';

const cs = classNames.bind(styles);

const FLOATING_CONTACT_BOTTOM_CSS_VARIABLE = '--floating-contact-bottom';
const COOKIE_CONSENT_BOTTOM_PX = 24;
const FLOATING_CONTACT_COOKIE_GAP_PX = 32;

const categoryItems: Array<{ key: CookieConsentCategory; label: string }> = [
  { key: 'necessary', label: 'Necessary' },
  { key: 'preferences', label: 'Preferences' },
  { key: 'statistics', label: 'Statistics' },
  { key: 'marketing', label: 'Marketing' },
];

const defaultSelection: CookieConsentCategories = {
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false,
};

function subscribeToConsentChanges(onStoreChange: () => void) {
  const timer = window.setTimeout(onStoreChange, 0);

  window.addEventListener('focus', onStoreChange);
  window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT_NAME, onStoreChange);

  return () => {
    window.clearTimeout(timer);
    window.removeEventListener('focus', onStoreChange);
    window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT_NAME, onStoreChange);
  };
}

function getCookieConsentVisibleSnapshot() {
  return readCookieConsent(document.cookie) === null;
}

function getServerCookieConsentVisibleSnapshot() {
  return false;
}

function CookieCategoryIndicator({
  categoryKey,
  label,
  checked,
  disabled = false,
  onChange,
}: {
  categoryKey: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}) {
  return (
    <label className={cs('category')}>
      <span className={cs('checkboxWrap')}>
        <input
          type='checkbox'
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange?.(event.target.checked)}
          aria-label={label}
          className={cs('checkboxInput')}
          data-cookie-category={categoryKey}
        />
        <span aria-hidden className={cs('checkboxBox', { checked })}>
          {checked ? (
            <svg
              width='11'
              height='11'
              viewBox='0 0 11 11'
              fill='none'
              aria-hidden='true'
              className={cs('checkboxTick')}
            >
              <path
                d='M2.75 5.6L4.6 7.45L8.25 3.75'
                stroke='#f62056'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.3'
              />
            </svg>
          ) : null}
        </span>
      </span>
      <span className={cs('categoryLabel')}>{label}</span>
    </label>
  );
}

export function CookieConsentGate() {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const consentMissing = useSyncExternalStore(
    subscribeToConsentChanges,
    getCookieConsentVisibleSnapshot,
    getServerCookieConsentVisibleSnapshot,
  );
  const [dismissed, setDismissed] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const [attentionRequestId, setAttentionRequestId] = useState(0);
  const [selectedCategories, setSelectedCategories] =
    useState<CookieConsentCategories>(defaultSelection);
  const isVisible = (consentMissing || forceOpen) && !dismissed;
  const isVisibleRef = useRef(isVisible);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    const rootStyle = document.documentElement.style;

    if (!isVisible) {
      rootStyle.removeProperty(FLOATING_CONTACT_BOTTOM_CSS_VARIABLE);
      return;
    }

    const panelElement = panelRef.current;

    if (!panelElement) {
      return;
    }

    const syncFloatingContactPosition = () => {
      const panelHeight = panelElement.getBoundingClientRect().height;
      const nextBottom = COOKIE_CONSENT_BOTTOM_PX + panelHeight + FLOATING_CONTACT_COOKIE_GAP_PX;

      rootStyle.setProperty(FLOATING_CONTACT_BOTTOM_CSS_VARIABLE, `${nextBottom}px`);
    };
    const resizeObserver =
      typeof ResizeObserver === 'function' ? new ResizeObserver(syncFloatingContactPosition) : null;

    syncFloatingContactPosition();
    resizeObserver?.observe(panelElement);
    window.addEventListener('resize', syncFloatingContactPosition);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncFloatingContactPosition);
      rootStyle.removeProperty(FLOATING_CONTACT_BOTTOM_CSS_VARIABLE);
    };
  }, [isVisible, attentionRequestId]);

  useEffect(() => {
    function handleConsentRequest(event: Event) {
      const shouldShake = isVisibleRef.current;
      const isAuthRequest = event instanceof CustomEvent && event.detail?.reason === 'auth';

      setDismissed(false);
      if (isAuthRequest) {
        setForceOpen(true);
      }
      if (shouldShake) {
        setAttentionRequestId((currentId) => currentId + 1);
      }
    }

    window.addEventListener(COOKIE_CONSENT_REQUEST_EVENT_NAME, handleConsentRequest);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_REQUEST_EVENT_NAME, handleConsentRequest);
    };
  }, []);

  function updateSelectedCategory(categoryKey: CookieConsentCategory, checked: boolean) {
    setSelectedCategories((currentCategories) => ({
      ...currentCategories,
      [categoryKey]: checked,
    }));
  }

  function acceptCookies(choice: 'necessary' | 'selection' | 'all') {
    const categories =
      choice === 'all'
        ? { necessary: true, preferences: true, statistics: true, marketing: true }
        : choice === 'selection'
          ? selectedCategories
          : { necessary: true, preferences: false, statistics: false, marketing: false };
    const recordChoice =
      !categories.necessary &&
      !categories.preferences &&
      !categories.statistics &&
      !categories.marketing
        ? 'rejected'
        : choice;

    writeCookieConsent(
      document,
      createCookieConsentRecord(recordChoice, Date.now(), categories),
      window.location,
    );
    setForceOpen(false);
    window.dispatchEvent(new Event(COOKIE_CONSENT_CHANGE_EVENT_NAME));
  }

  if (!isVisible) {
    return null;
  }

  return (
    <section aria-label='Cookie consent' className={cs('root')} data-testid='cookie-consent-banner'>
      <div className={cs('positioner')}>
        <div
          ref={panelRef}
          key={attentionRequestId}
          className={cs('panel', { panelAttention: attentionRequestId > 0 })}
        >
          <div className={cs('content')}>
            <div className={cs('body')}>
              <div className={cs('titleRow')}>
                <h2 className={cs('title')}>This website uses cookies</h2>
                <button
                  type='button'
                  aria-label='Close cookie banner'
                  onClick={() => setDismissed(true)}
                  className={cs('closeButton')}
                >
                  <img
                    src='/images/cookie-consent/close.svg'
                    alt=''
                    aria-hidden='true'
                    className={cs('closeIcon')}
                  />
                </button>
              </div>

              <p className={cs('description')}>
                We use cookies to personalise content and ads, to provide social media features and
                to analyse our traffic. We also share information about your use of our site with
                our social media, advertising and analytics partners who may combine it with other
                information that you&apos;ve provided to them or that they&apos;ve collected from
                your use of their services.
              </p>

              <div className={cs('categories')}>
                {categoryItems.map((item) => (
                  <CookieCategoryIndicator
                    key={item.key}
                    categoryKey={item.key}
                    label={item.label}
                    checked={selectedCategories[item.key]}
                    onChange={(checked) => updateSelectedCategory(item.key, checked)}
                  />
                ))}
              </div>
            </div>

            <div className={cs('actions')}>
              <button
                type='button'
                onClick={() => acceptCookies('necessary')}
                className={cs('secondaryButton')}
              >
                Use necessary cookies only
              </button>
              <button
                type='button'
                onClick={() => acceptCookies('selection')}
                className={cs('secondaryButton')}
              >
                Allow selection
              </button>
              <button
                type='button'
                onClick={() => acceptCookies('all')}
                className={cs('primaryButton')}
              >
                Allow all cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
